import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { safeLog } from '@/utils/safeLogger';

export function TestClockSettings() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('admin_settings')
        .select('enable_test_clock')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      setIsEnabled(data?.enable_test_clock ?? false);
    } catch (error) {
      console.error('Error loading test clock settings:', error);
      toast.error('Failed to load test clock settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    try {
      setIsSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          user_id: user.id,
          enable_test_clock: enabled,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setIsEnabled(enabled);
      toast.success(
        enabled ? 'Test Clock enabled' : 'Test Clock disabled',
        {
          description: enabled 
            ? 'New subscriptions will use Stripe Test Clocks with simulated time'
            : 'New subscriptions will use real monthly/annual periods'
        }
      );
    } catch (error) {
      console.error('Error saving test clock settings:', error);
      toast.error('Failed to save test clock settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="glass-card-strong">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <div>
            <CardTitle>Test Clock Settings</CardTitle>
            <CardDescription>
              Control whether new subscriptions use Stripe Test Clocks
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
          <div className="flex-1">
            <h3 className="text-sm font-semibold mb-1">Enable Test Clock for New Subscriptions</h3>
            <p className="text-xs text-muted-foreground">
              {isEnabled 
                ? "New subscriptions will use Test Clocks with 1-day periods for testing" 
                : "New subscriptions will have realistic monthly/annual periods"}
            </p>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={isLoading || isSaving}
            className="data-[state=checked]:bg-blue-600"
          />
        </div>

        {isEnabled ? (
          <Alert className="border-blue-500/50 bg-blue-500/10">
            <CheckCircle2 className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <strong>Test Clock Enabled:</strong> New subscriptions will be created with Stripe Test Clocks. 
              You can use the Test Clock Manager to simulate time and test renewals.
              <br /><br />
              <span className="text-xs text-muted-foreground">
                Note: Existing subscriptions are not affected. Only new subscriptions created after enabling this setting.
              </span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-green-500/50 bg-green-500/10">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <strong>Real Periods Enabled:</strong> New subscriptions will have realistic monthly/annual billing periods.
              This matches production behavior.
              <br /><br />
              <span className="text-xs text-muted-foreground">
                ✅ <strong>Monthly subscriptions</strong> will renew every 30 days<br />
                ✅ <strong>Annual subscriptions</strong> will renew every 365 days<br />
                ✅ No Test Clock simulation
              </span>
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>How it works:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Enabled:</strong> Creates Test Clock with 1-day billing periods for quick testing</li>
              <li><strong>Disabled:</strong> Uses real 30-day/365-day periods (production-like behavior)</li>
              <li>This setting only affects <strong>new subscriptions</strong> created after toggling</li>
              <li>To apply to existing subscriptions, cancel and recreate them</li>
            </ul>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
